import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchCaseResourceHistory, fetchAllResources, postResourceAssignment, fetchCaseResources } from "../../../../services/httpClient";
import ToastMsg from "../../../ui/ToastMsg";
import { Button, Card, Col, Container, Row, Spinner, Stack, Table } from "react-bootstrap";
import ResourceCard from "./ResourceCard";
import SelectFamily from "../../../ui/SelectFamily";
import SelectObjectTypeahead from "../../../ui/SelectObjectTypeahead";
import SelectObject from "../../../ui/SelectObject";
import ReactPaginate from "react-paginate";
import ResourceLink from "./ResourceLink";

interface ResourcesState {
  viewResources: boolean;
  setViewResources: any;
  viewHistory: boolean;
  setViewHistory: any;
  assignResource: boolean;
  setAssignResource: any;
  getData: boolean;
  family: any;
}

const itemsPerPage = 15;

const Resources = ({
  viewResources,
  setViewResources,
  viewHistory,
  setViewHistory,
  assignResource,
  setAssignResource,
  getData,
  family,
}: ResourcesState) => {
  const { id } = useParams();
  const [resourceAssignments, setResourceAssignments] = useState<any[]>([]);
  const [history, setHistory] = useState<any[]>([]);
  const [resourceOptions, setResourceOptions] = useState<any[]>([]);
  const [allResources, setAllResources] = useState<any[]>([]);
  const [selectedResource, setSelectedResource] = useState<any>(null);
  const [quantitiesAvailable, setQuantitiesAvailable] = useState<any[]>([]);
  const [assignedTo, setAssignedTo] = useState<string>("0");
  const [quantityToAssign, setQuantityToAssign] = useState<number>(1);
  const [allowAssignResource, setAllowAssignResource] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastBody, setToastBody] = useState<string>("");
  const [toastText, setToastText] = useState<string>("");
  const [errMsg, setErrMsg] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);

  const hasResources = resourceAssignments.some((a: any) => a.resources.length > 0);
  const pageCount = Math.ceil(history.length / itemsPerPage);
  const offset = currentPage * itemsPerPage;
  const currentHistory = history.slice(offset, offset + itemsPerPage);

  const updateViewResources = (e: any) => {
    setErrMsg("");
    setViewResources(e);
    setViewHistory(false);
    setAssignResource(false);
  };

  const updateViewHistory = (e: any) => {
    setErrMsg("");
    setViewResources(false);
    setViewHistory(e);
    setAssignResource(false);
  };

  useEffect(() => {
    if (getData) {
      setIsLoading(true);
      getResourceAssignments();
      getHistory();
      getResources();
    }
  }, [getData]);

  useEffect(() => {
    const validInput = selectedResource !== null && assignedTo !== "0";
    validInput ? setAllowAssignResource(true) : setAllowAssignResource(false);
  }, [selectedResource, assignedTo]);

  const getResourceAssignments = () => {
    fetchCaseResources(id)
      .then((res: any) => {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const assignments = res[0].data.map((a: any) => ({
          ...a,
          nonconsumables: a.resources.filter((r: any) => r.resource_type_key === "nonconsumable"),
          consumables: a.resources.filter((r: any) => r.resource_type_key === "consumable" && new Date(r.assigned_at) >= thirtyDaysAgo),
        }));

        setResourceAssignments(assignments);
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getHistory = () => {
    fetchCaseResourceHistory(id)
      .then((res: any) => {
        const history = res[0].data;
        setHistory(history);
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const familyMembers = useMemo(() => {
    const normalized = family
      .filter((f: any) => !f.deleted_at)
      .map((f: any) => ({
        id: f.id,
        type: f.role === "family" ? "guardian" : "member",
        key: `${f.firstname} ${f.lastname}`,
        name: `${f.firstname} ${f.lastname}`,
      }));

    return [...normalized];
  }, [family]);

  const getResources = () => {
    fetchAllResources()
      .then((res: any) => {
        const resources = res[0].data.filter((r: any) => !r.deleted_at);
        setAllResources(resources);

        const consumables = resources
          .filter((r: any) => r.type_key === "consumable" && r.quantity > 0)
          .map((r: any) => ({
            value: r.id,
            label: r.category_name ? r.category_name + " - " + r.name : r.name,
            original: r,
          }));

        const nonConsumables = resources
          .filter((r: any) => r.type_key === "nonconsumable")
          .map((r: any) => ({
            value: r.id,
            label: r.category_name ? r.category_name + " - " + r.name : r.name,
            original: r,
            isDisabled: r.remaining === 0,
          }))
          .sort((a: any, b: any) => {
            return Number(a.isDisabled) - Number(b.isDisabled);
          });

        const groupedResources = [
          { label: "Consumable", options: consumables },
          { label: "Non-Consumable", options: nonConsumables },
        ];

        setResourceOptions(groupedResources);
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getAvailableQuantity = (resourceId: number, resourceTypeKey: string): number => {
    const originalResource = allResources.find((r: any) => r.id === resourceId);
    if (!originalResource) return 0;
    return originalResource.remaining;
  };

  const formatResourceName = (name: string) => {
    if (name.endsWith("s")) {
      return name.replace(/s$/, "(s)");
    }
    return `${name}(s)`;
  };

  const onClickSaveResourceAssignment = () => {
    setErrMsg("");

    const newAssignment: any = {
      resource_id: Number(selectedResource.original.id),
      quantity: Number(quantityToAssign),
    };

    const [idValue, typeValue] = assignedTo.split("|");

    typeValue === "guardian" ? (newAssignment.user_id = Number(idValue)) : (newAssignment.member_id = Number(idValue));

    postResourceAssignment(newAssignment, id)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Resource Assigned");
        setToastBody(`${quantityToAssign} ${formatResourceName(selectedResource.original.name)}`);
        setSelectedResource(null);
        setQuantityToAssign(1);
        setQuantitiesAvailable([]);
        setAssignResource(false);
        if (familyMembers.length === 1) {
          setAssignedTo(`${familyMembers[0].id}|${familyMembers[0].type}`);
        } else {
          setAssignedTo("0");
        }
        getResourceAssignments();
        getHistory();
        getResources();
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickAssignResource = () => {
    setErrMsg("");
    setAssignResource(true);
    setSelectedResource(null);
    setQuantityToAssign(1);
    setQuantitiesAvailable([]);

    if (familyMembers.length === 1) {
      setAssignedTo(`${familyMembers[0].id}|${familyMembers[0].type}`);
    } else {
      setAssignedTo("0");
    }
  };

  const onClickAssignResourceCancel = () => {
    setErrMsg("");
    setAssignResource(false);
  };

  const updateSelectedResource = (e: any) => {
    const formattedResource = {
      value: e.id,
      label: e.name,
      original: e,
    };

    const available = e.type_key === "consumable" ? e.quantity : e.remaining;
    const quantityOptions = Array.from({ length: available }, (_, i) => ({
      key: i + 1,
      name: i + 1,
    }));

    setSelectedResource(formattedResource);
    setQuantitiesAvailable(quantityOptions);
  };

  const formatHistoryDateTime = (dateTime: string) => {
    const utcString = dateTime.endsWith("Z") ? dateTime : `${dateTime}Z`;
    const localTime = new Date(utcString);

    const date = localTime.toLocaleDateString("en-US", {
      weekday: "short",
      month: "long",
      day: "numeric",
    });

    const time = localTime
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();

    return { date, time };
  };

  const handlePageClick = (event: { selected: number }) => {
    setCurrentPage(event.selected);
  };

  return (
    <>
      <ToastMsg showToast={showToast} setShowToast={setShowToast} toastText={toastText} toastBody={toastBody} />
      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          {viewResources && (
            <>
              <Row>
                <Col xs={1} lg={3}>
                  <h4 className="app-header-text">Resources</h4>
                </Col>
                <Col xs={11} lg={9} className="text-end">
                  {!assignResource && (
                    <Button className="app-secondary-color me-3 button" onClick={() => updateViewHistory(true)}>
                      History
                    </Button>
                  )}
                  <Button
                    className="app-primary-color button"
                    onClick={assignResource ? onClickAssignResourceCancel : onClickAssignResource}
                  >
                    {assignResource ? "Cancel" : "Assign Resource"}
                  </Button>
                </Col>
              </Row>
              {assignResource && (
                <Card className="bg-light mb-3">
                  <Card.Body>
                    <Stack direction="horizontal" gap={3}>
                      <div style={{ width: "40%" }}>
                        <SelectObjectTypeahead
                          label="Resource"
                          value={selectedResource}
                          options={resourceOptions}
                          onUpdate={(e: any) => updateSelectedResource(e)}
                          focused
                          groupHeading
                        />
                      </div>
                      <div style={{ width: "40%" }}>
                        <SelectFamily
                          skipDefaultSelection={familyMembers.length === 1}
                          family={familyMembers}
                          familyUser={assignedTo}
                          updateFamilyUser={(e: any) => setAssignedTo(e.target.value)}
                          fullSize
                        />
                      </div>
                      <div style={{ width: "15%" }}>
                        <SelectObject
                          testId="resource-quantity"
                          initialOption="Select"
                          label="Quantity"
                          value={quantityToAssign}
                          options={quantitiesAvailable}
                          onUpdate={(e: any) => setQuantityToAssign(e.target.value)}
                        />
                      </div>
                      <Button
                        className="app-secondary-color button"
                        onClick={onClickSaveResourceAssignment}
                        disabled={!allowAssignResource}
                      >
                        Assign
                      </Button>
                    </Stack>
                  </Card.Body>
                </Card>
              )}
              <Row>
                <Col xs={12}>
                  {!hasResources ? (
                    <p className="mt-2">No Resources Currently Assigned</p>
                  ) : (
                    resourceAssignments.map((assignment: any, index: number) => (
                      <>
                        {familyMembers.length > 1 && (assignment.nonconsumables.length > 0 || assignment.consumables.length > 0) && (
                          <h5 className="app-header-text mb-2">{assignment.assignee_name}</h5>
                        )}
                        {assignment.nonconsumables.map((resource: any, index: number) => (
                          <ResourceCard
                            key={index}
                            resource={resource}
                            assignedQuantity={resource.quantity}
                            assigneeId={assignment.assignee_id}
                            assigneeType={assignment.assignee_type}
                            getResources={getResources}
                            getResourceAssignments={getResourceAssignments}
                            getHistory={getHistory}
                            formatResourceName={formatResourceName}
                            setShowToast={setShowToast}
                            setToastText={setToastText}
                            setToastBody={setToastBody}
                            setErrMsg={setErrMsg}
                            availableQuantity={getAvailableQuantity(resource.resource_id, resource.resource_type_key)}
                          />
                        ))}
                        {assignment.consumables.length > 0 && (
                          <ResourceLink resources={assignment.consumables} formatHistoryDateTime={formatHistoryDateTime} />
                        )}
                      </>
                    ))
                  )}
                </Col>
              </Row>
            </>
          )}
          {viewHistory && (
            <>
              <Row className="mb-0">
                <Col>
                  <h4 className="app-header-text">History</h4>
                </Col>
                <Col className="text-end">
                  <Button className="app-primary-color button" onClick={() => updateViewResources(true)}>
                    Back
                  </Button>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col xs={12}>
                  {currentHistory.length === 0 ? (
                    <p className="mt-2">No History</p>
                  ) : (
                    <>
                      <Table striped>
                        <thead>
                          <tr>
                            <th>Resource</th>
                            <th>Category</th>
                            <th>Quantity</th>
                            <th>Person</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentHistory.map((a: any, index: number) => {
                            const formatted = formatHistoryDateTime(a.agg_assigned_at ?? a.agg_returned_at);
                            const action = a.agg_assigned_at !== null ? "Assigned" : "Returned";

                            return (
                              <tr key={index}>
                                <td>{a.resource_name}</td>
                                <td className="text-capitalize">{a.resource_category_name}</td>
                                <td>{a.count}</td>
                                <td>{a.assignee_name}</td>
                                <td>{`${action} on ${formatted.date} at ${formatted.time}`}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                      {pageCount > 1 && (
                        <div id="programs-pagination">
                          <ReactPaginate
                            previousLabel={"← Previous"}
                            nextLabel={"Next →"}
                            pageCount={pageCount}
                            onPageChange={handlePageClick}
                            containerClassName={"pagination justify-content-center"}
                            pageClassName={"page-item"}
                            pageLinkClassName={"page-link"}
                            previousClassName={"page-item"}
                            previousLinkClassName={"page-link"}
                            nextClassName={"page-item"}
                            nextLinkClassName={"page-link"}
                            activeClassName={"active"}
                            forcePage={currentPage}
                          />
                        </div>
                      )}
                    </>
                  )}
                </Col>
              </Row>
            </>
          )}
        </>
      )}
    </>
  );
};

export default Resources;
